import { useEffect, useState } from 'react';
import { ApiError, policyRuleDraft, policyRulePreview, type PolicyGuard, type PolicyReplay, type RuleAction, type RuleAudience, type RuleSignal, type RuleSpec, type RuleWindow } from '@aithora/shared';
import { useSession } from '../../session/SessionContext';
import { useDemoBuilding } from '../../session/DemoScope';
import { ACTIONS, ACTIONS_SHORT, AUDIENCES, SIGNALS, WINDOWS } from './catalog';
import { clock, pre, sx } from './util';
import { BuildLabel } from '../../ui/buildLabels';

/** "Mon 2:14 PM": the replay's catches fall inside one week, so the weekday names the day. */
const weekdayClock = (iso: string) => new Date(iso).toLocaleDateString('en-US', { weekday: 'short' }) + ' ' + clock(iso);

type Rule = Pick<RuleSpec, 'signal' | 'threshold' | 'window' | 'audience' | 'action'> & { mode?: string };

export function ruleSentence(r: Rule): string {
  return 'When ' + SIGNALS[r.signal] + ' reach ' + r.threshold + ' ' + WINDOWS[r.window] + ' for ' + AUDIENCES[r.audience] + ', ' + ACTIONS[r.action] + '.';
}

export function ruleName(r: Rule): string {
  const s = SIGNALS[r.signal];
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** The built-in guards that already watch a signal (server: SIGNAL_OVERLAPS). */
const OVERLAP: Record<RuleSignal, string | null> = { money: 'spend', medical: 'conf', unknown: 'allow', distress: 'appr', night: null, share: 'juris' };

/**
 * Read a plain request into the sentence's parts, on this device. It is a keyword reading
 * of what was typed (the design's parseLocal), shown highlighted for the person to check.
 */
export function parsePlain(t: string): Partial<RuleSpec> {
  const x = t.toLowerCase();
  const o: Partial<RuleSpec> = {};
  if (/money|gift|bank|pay|card|cash|wire|pin/.test(x)) o.signal = 'money';
  else if (/dose|medic|pill|symptom|drug/.test(x)) o.signal = 'medical';
  else if (/unknown|stranger|new caller|not on|anyone new|someone new/.test(x)) o.signal = 'unknown';
  else if (/sad|hurt|harm|distress|suicid|giving up|lonely/.test(x)) o.signal = 'distress';
  else if (/night|late|2am|3am|midnight/.test(x)) o.signal = 'night';
  else if (/share|health detail|record|diagnos/.test(x)) o.signal = 'share';
  if (/memory|dementia/.test(x)) o.audience = 'memory';
  else if (/new|first month|first 30/.test(x)) o.audience = 'new';
  else if (/every|all/.test(x)) o.audience = 'all';
  if (/family|daughter|son/.test(x)) o.action = 'family';
  else if (/nurse/.test(x)) o.action = 'nurse';
  else if (/log|just record|note/.test(x)) o.action = 'log';
  else if (/stop|block|person|human|staff/.test(x)) o.action = 'human';
  if (/stop|never|block|any /.test(x)) o.threshold = 1;
  const m = x.match(/(\d+)\s*(times|x|requests|calls)/);
  if (m) o.threshold = Math.max(1, Math.min(20, +m[1]));
  if (/hour/.test(x)) o.window = '1h';
  else if (/week/.test(x)) o.window = '7d';
  else if (/day|today/.test(x)) o.window = '24h';
  return o;
}

type Slot = 'signal' | 'window' | 'audience' | 'action';

/** "New rule" (design pv.showNew, T12545): say it in plain words, or tap a part to change it. */
export function RuleBuilder({ onCancel, onAdd, guards, armedOf }: { onCancel: () => void; onAdd: (r: RuleSpec) => void; guards: PolicyGuard[]; armedOf: (g: PolicyGuard) => boolean }) {
  const demoBuilding = useDemoBuilding();
  const [nb, setNb] = useState<RuleSpec>({ signal: 'money', threshold: 3, window: '24h', audience: 'new', action: 'human', mode: 'watch' });
  const [text, setText] = useState('');
  const [slot, setSlot] = useState<Slot | null>(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const { client } = useSession();
  // Atlas drafts the parts (POST /policy/rules/draft). Until that route is served (404) the
  // words are read on this device instead, and the note says which one drafted it.
  const draft = async () => {
    const t = text.trim();
    if (!t || busy) return;
    setBusy(true);
    let parts: Partial<RuleSpec> | null = null;
    let byAtlas = false;
    try {
      const r = await policyRuleDraft(client, { body: { text: t } });
      parts = r.draft ?? {};
      byAtlas = true;
    } catch (e) {
      if (!(e instanceof ApiError) || e.status !== 404) {
        setBusy(false);
        setNote('Atlas could not draft it just now. Tap each part of the sentence to set it.');
        return;
      }
    }
    if (!parts || !Object.keys(parts).length) parts = parsePlain(t);
    setBusy(false);
    setNb((p) => ({ ...p, ...parts }));
    setSlot(null);
    setNote((byAtlas ? 'Atlas drafted this' : 'Drafted') + ' from your words. Check each highlighted part before adding it.');
  };
  const slotBtn = (k: Slot, label: string) => (
    <a
      href="#"
      aria-expanded={slot === k}
      onClick={(e) => { pre(e); setSlot(slot === k ? null : k); }}
      style={sx('display:inline-flex;align-items:center;min-height:40px;padding:0 12px;border-radius:10px;font-size:16px;font-weight:700;text-decoration:none;' + (slot === k ? 'background:#2F5C97;color:#fff;' : 'background:rgba(47,92,151,.10);color:#264C7E;box-shadow:inset 0 -2px 0 rgba(47,92,151,.45);'))}
    >
      {label}
    </a>
  );
  const opts: Record<Slot, [string, string][]> = {
    signal: Object.entries(SIGNALS),
    window: Object.entries(WINDOWS),
    audience: Object.entries(AUDIENCES),
    action: Object.entries(ACTIONS),
  };
  // What the rule would have caught in the last 7 days, before it is saved
  // (POST /policy/rules/preview). Until that route is served the note below says when replay runs.
  const [preview, setPreview] = useState<PolicyReplay | null>(null);
  useEffect(() => {
    const ctrl = new AbortController();
    const t = window.setTimeout(() => {
      const { signal: sig, threshold, window: win, audience, action, mode } = nb;
      policyRulePreview(client, { query: { days: 7 }, body: { signal: sig, threshold, window: win, audience, action, mode }, signal: ctrl.signal })
        .then((r) => { if (!ctrl.signal.aborted) setPreview(r); })
        .catch(() => { if (!ctrl.signal.aborted) setPreview(null); });
    }, 250);
    return () => { ctrl.abort(); window.clearTimeout(t); };
  }, [client, nb]);
  const ovKey = OVERLAP[nb.signal];
  const ov = ovKey ? guards.find((g) => g.key === ovKey) ?? null : null;
  return (
    <div data-testid="rule-builder" style={sx('display:flex;flex-direction:column;gap:12px;min-width:0;animation:riseIn .26s cubic-bezier(.22,.61,.36,1);')}>
      <div style={sx('display:flex;align-items:baseline;gap:6px 12px;flex-wrap:wrap;padding-bottom:8px;border-bottom:1px solid rgba(34,39,46,.14);')}>
        <span style={sx('font-size:17px;font-weight:800;letter-spacing:-.01em;color:#22272E;')}>New rule</span>
        <span style={sx('font-size:16px;color:#54606C;flex:1 1 200px;min-width:0;')}>Say it in plain words, or tap any part of the sentence to change it.</span>
        <a href="#" onClick={(e) => { pre(e); onCancel(); }} style={sx('display:inline-flex;align-items:center;min-height:36px;font-size:16px;font-weight:700;color:#54606C;text-decoration:none;')}>
          Cancel
        </a>
      </div>
      <div style={sx('display:flex;gap:8px;flex-wrap:wrap;min-width:0;')}>
        <input
          type="text"
          aria-label="Describe the rule"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); draft(); } }}
          placeholder="e.g. Stop Atlas talking about money with anyone new"
          style={sx('flex:1 1 320px;min-width:0;height:44px;box-sizing:border-box;padding:0 14px;border:1px solid rgba(34,39,46,.22);border-radius:10px;background:#FCFAF4;font-family:inherit;font-size:16px;color:#22272E;outline:none;')}
        />
        <a href="#" onClick={(e) => { pre(e); draft(); }} style={sx('display:inline-flex;align-items:center;min-height:44px;padding:0 16px;border-radius:10px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;' + (text.trim() ? 'background:#22272E;color:#FCFAF4;' : 'background:rgba(34,39,46,.08);color:#54606C;'))}>
          {busy ? 'Drafting…' : 'Draft with Atlas'}
        </a>
      </div>
      {note && <span style={sx('font-size:16px;font-weight:700;color:#2F5C97;')}>{note}</span>}
      <div style={sx('display:flex;align-items:center;gap:6px 8px;flex-wrap:wrap;font-size:16px;line-height:1.5;color:#22272E;padding:4px 0;')}>
        <span>When</span>
        {slotBtn('signal', SIGNALS[nb.signal])}
        <span>reach</span>
        <span style={sx('display:inline-flex;align-items:center;border:1px solid rgba(34,39,46,.2);border-radius:10px;overflow:hidden;')}>
          <a href="#" aria-label="Fewer" onClick={(e) => { pre(e); setNb((p) => ({ ...p, threshold: Math.max(1, p.threshold - 1) })); }} style={sx('display:inline-flex;align-items:center;justify-content:center;width:36px;height:40px;font-size:17px;font-weight:700;color:#264C7E;text-decoration:none;')}>
            {'−'}
          </a>
          <span style={sx('min-width:30px;text-align:center;font-weight:800;font-variant-numeric:tabular-nums;')}>{nb.threshold}</span>
          <a href="#" aria-label="More" onClick={(e) => { pre(e); setNb((p) => ({ ...p, threshold: Math.min(20, p.threshold + 1) })); }} style={sx('display:inline-flex;align-items:center;justify-content:center;width:36px;height:40px;font-size:17px;font-weight:700;color:#264C7E;text-decoration:none;')}>
            +
          </a>
        </span>
        {slotBtn('window', WINDOWS[nb.window])}
        <span>for</span>
        {slotBtn('audience', AUDIENCES[nb.audience])}
        <span>, then</span>
        {slotBtn('action', ACTIONS[nb.action])}
        <span>.</span>
      </div>
      {slot && (
        <div style={sx('display:flex;flex-wrap:wrap;gap:6px;padding:8px 10px;border-radius:10px;background:rgba(47,92,151,.06);')}>
          {opts[slot].map(([k, label]) => (
            <a
              key={k}
              href="#"
              onClick={(e) => {
                pre(e);
                setSlot(null);
                setNb((p) => ({ ...p, [slot]: k as RuleSignal & RuleWindow & RuleAudience & RuleAction }));
              }}
              style={sx('display:inline-flex;align-items:center;min-height:40px;padding:0 13px;border-radius:9px;font-size:16px;font-weight:700;text-decoration:none;' + ((nb as unknown as Record<string, string>)[slot] === k ? 'background:#2F5C97;color:#fff;' : 'color:#264C7E;border:1px solid rgba(47,92,151,.35);'))}
            >
              {label}
            </a>
          ))}
        </div>
      )}
      <div style={sx('display:flex;align-items:center;gap:8px 14px;flex-wrap:wrap;')}>
        <span style={sx('display:inline-flex;border:1px solid rgba(34,39,46,.18);border-radius:10px;overflow:hidden;')}>
          {(
            [
              ['watch', 'Watch only'],
              ['enforce', 'Enforce'],
            ] as const
          ).map(([m, label]) => (
            <a key={m} href="#" aria-pressed={nb.mode === m} onClick={(e) => { pre(e); setNb((p) => ({ ...p, mode: m })); }} style={sx('display:inline-flex;align-items:center;min-height:40px;padding:0 14px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;' + (nb.mode === m ? 'background:#22272E;color:#FCFAF4;' : 'color:#3C454F;'))}>
              {label}
            </a>
          ))}
        </span>
        <span style={sx('font-size:16px;color:#54606C;flex:1 1 240px;min-width:0;line-height:1.45;text-wrap:pretty;')}>
          {nb.mode === 'watch' ? 'Records what it would have done, without acting. Switch it to enforce once the replay looks right.' : 'Acts on every match as soon as it is saved and approved.'}
        </span>
      </div>
      {ov && (
        <div style={sx('display:flex;gap:10px;align-items:baseline;padding:9px 12px;border-radius:10px;background:rgba(34,39,46,.10);')}>
          <span style={sx('font-size:14px;font-weight:800;letter-spacing:.08em;color:#3C454F;white-space:nowrap;')}>OVERLAP</span>
          <span style={sx('font-size:16px;color:#22272E;line-height:1.45;text-wrap:pretty;')}>
            {'“' + ov.name + '” already watches this' + (armedOf(ov) ? '' : ' (it is switched off right now)') + '. Both will run; when they disagree, the stricter action wins.'}
          </span>
        </div>
      )}
      <div style={sx('display:flex;flex-direction:column;gap:4px;min-width:0;border-top:1px solid rgba(34,39,46,.12);padding-top:10px;')}>
        <span style={sx('display:flex;align-items:baseline;gap:10px;flex-wrap:wrap;')}>
          <span style={sx('font-size:14px;font-weight:700;letter-spacing:.12em;color:#54606C;')}>REPLAY {'·'} LAST 7 DAYS</span>
          {preview && !preview.real && !demoBuilding && <BuildLabel><span style={sx('font-size:14px;font-weight:700;letter-spacing:.06em;color:#54606C;opacity:.8;')}>SAMPLE</span></BuildLabel>}
          {preview && preview.supported && preview.total !== null && (
            <span style={sx('font-size:16px;font-weight:800;color:#22272E;')}>{preview.total === 0 ? 'Would not have fired' : 'Would have fired ' + preview.total + (preview.total === 1 ? ' time' : ' times')}</span>
          )}
        </span>
        {preview && preview.supported ? (
          preview.catches.slice(0, 3).map((c, i) => (
            <span key={i} style={sx('display:grid;grid-template-columns:minmax(0,150px) minmax(0,1fr) auto;gap:4px 14px;align-items:baseline;padding:7px 0;border-top:1px solid rgba(34,39,46,.08);min-width:0;')}>
              <b style={sx('font-size:16px;color:#22272E;min-width:0;')}>{[c.resident.initials, c.resident.room ? 'Rm ' + c.resident.room : null].filter(Boolean).join(' · ')}</b>
              <span style={sx('font-size:16px;color:#3C454F;line-height:1.4;min-width:0;text-wrap:pretty;')}>{c.excerpt ?? '–'}</span>
              <span style={sx('display:flex;flex-direction:column;align-items:flex-end;')}>
                <span style={sx('font-size:14px;color:#54606C;white-space:nowrap;')}>{weekdayClock(c.at)}</span>
                <span style={sx('font-size:14px;font-weight:700;color:#2F5C97;white-space:nowrap;')}>{(c.wouldHaveOnly ? 'Would have: ' : '') + ACTIONS_SHORT[c.action]}</span>
              </span>
            </span>
          ))
        ) : (
          <span style={sx('font-size:16px;color:#54606C;line-height:1.45;text-wrap:pretty;')}>
            {preview && !preview.supported
              ? [...preview.unsupportedSignals.map((u) => u.reason), preview.unsupportedAudience?.reason].filter(Boolean).join(' ')
              : 'Replay runs on real records once the rule is saved: open it under Your rules to see what it would have caught.'}
          </span>
        )}
      </div>
      <div style={sx('display:flex;align-items:center;gap:10px;flex-wrap:wrap;')}>
        <a href="#" onClick={(e) => { pre(e); onAdd(nb); }} style={sx('display:inline-flex;align-items:center;min-height:44px;padding:0 18px;border-radius:10px;background:#2F5C97;color:#fff;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;')}>
          Add as draft
        </a>
        <span style={sx('font-size:14px;color:#54606C;')}>Nothing goes live until you review and save below.</span>
      </div>
    </div>
  );
}
